// ---------- le premier contact : une bulle à la fois, sur les toutes premières parties ----------
//
// Le jeu ne s'explique pas d'avance : un écran de règles se saute, et on ne lit pas un mode
// d'emploi avant d'avoir envie de jouer. Il montre plutôt, sur la partie elle-même, LA chose
// utile au moment où elle sert, et une seule à la fois. Depuis le jeu à l'IA (2026-11-04), la
// série dépend du MODE de la partie ouverte (shared/ai.ts) :
//  - première partie classique : l'indice de l'IA, le ruban, le compteur de coups ;
//  - deuxième partie classique : tenter le mot mystère ;
//  - première partie inversée : c'est toi qui fais deviner, puis les bonus.
// Chaque bulle ne s'affiche qu'une fois, sur l'appareil. C'est un confort et non une
// progression : il reste dans le localStorage et ne part pas avec la sauvegarde.
//
// La bulle ne masque rien de ce qu'on touche : elle se pose AU-DESSUS ou AU-DESSOUS de ce qu'elle
// montre, et seule elle prend le doigt (son bouton « Compris ») — le reste de l'écran reste jouable.

import type { PlayMode } from '../shared/ai';
import { t, type I18nKey } from './i18n';

type Tip = 'clue' | 'ribbon' | 'moves' | 'mystery' | 'inverse' | 'wallet';
/** Les séries de bulles, par mode : la n-ième partie de ce mode montre la première série dont une
 *  bulle n'a pas encore été vue. */
const STEPS: Record<PlayMode, Tip[][]> = {
  classic: [['clue', 'ribbon', 'moves'], ['mystery']],
  inverse: [['inverse', 'wallet']],
};
/** Ce que chaque bulle montre, dans l'écran de partie. */
const TARGET: Record<Tip, string> = {
  clue: '.dialog',
  ribbon: '.spine',
  moves: '.lane.on .moves',
  mystery: '.btn-mys:not([hidden])',
  inverse: '.dialog',
  wallet: '.wallet',
};

// `:v2` : les bulles de l'ancien jeu (`:v1`) ne disent plus rien du nouveau — tout le monde les revoit.
const KEY = 'verbigrid:coach:v2';

function seen(): Tip[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '[]') as Tip[];
  } catch {
    return [];
  }
}
function remember(tip: Tip): void {
  try {
    localStorage.setItem(KEY, JSON.stringify([...new Set([...seen(), tip])]));
  } catch {
    /* stockage refusé : la bulle reviendra, ce n'est pas grave */
  }
}

let bubble: HTMLElement | null = null;
let focused: Element | null = null;

function clear(): void {
  bubble?.remove();
  bubble = null;
  focused?.classList.remove('coach-focus');
  focused = null;
}

/** Une partie ouverte et son état chargé : la prochaine série de bulles de ce mode pas encore vue,
 *  s'il en reste. */
export function coach(root: HTMLElement, mode: PlayMode): void {
  clear();
  const done = new Set(seen());
  const step = STEPS[mode].find((s) => s.some((tip) => !done.has(tip)));
  if (!step) return;
  const queue = step.filter((tip) => !done.has(tip));
  // Après la mise en page : la bulle se place d'après ce qui est déjà dessiné.
  requestAnimationFrame(() => show(root, queue));
}

function show(root: HTMLElement, queue: Tip[]): void {
  clear();
  const tip = queue[0];
  if (!tip || root.hidden) return;
  const target = root.querySelector(TARGET[tip]);
  if (!target) return;
  focused = target;
  target.classList.add('coach-focus');
  const r = target.getBoundingClientRect();
  const el = document.createElement('div');
  el.className = 'coach';
  el.setAttribute('role', 'dialog');
  el.innerHTML = `<p>${t(`coach.${tip}` as I18nKey)}</p><button class="btn btn-go" type="button">${t('coach.ok')}</button><i class="coach-arrow"></i>`;
  document.body.append(el);
  bubble = el;
  // Au-dessus de la cible si la place y est (le ruban, le clavier), sinon dessous.
  const b = el.getBoundingClientRect();
  const above = r.top - b.height - 14 > 70;
  const top = above ? r.top - b.height - 14 : Math.min(innerHeight - b.height - 8, r.bottom + 14);
  const left = Math.max(
    10,
    Math.min(innerWidth - b.width - 10, r.left + r.width / 2 - b.width / 2),
  );
  el.style.top = `${top + scrollY}px`;
  el.style.left = `${left}px`;
  el.classList.add(above ? 'above' : 'below');
  el.style.setProperty('--ax', `${r.left + r.width / 2 - left}px`);
  el.querySelector('button')!.addEventListener('click', () => {
    remember(tip);
    show(root, queue.slice(1));
  });
}

/** « Revoir les bulles d'aide » (Réglages) : elles repartent de la première. */
export function resetCoach(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* stockage refusé : rien à oublier */
  }
}

/** En quittant la partie : la bulle part avec elle. */
export const dismissCoach = clear;
